import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SERVICE_CATEGORIES, HQ_STATUS } from '../../../config/ieumConfig';
import Pagination from '../../../components/common/Pagination';
import {
  DesktopListOnly, MobileCardField, MobileCardGrid, MobileCardHeader,
  MobileCardHeading, MobileCardLabel, MobileCardList, MobileCardSelect,
  MobileCardSubtitle, MobileCardTitle, MobileCardValue, MobileListCard,
  MobileListState,
} from '../../../components/common/AdminMobileList';
import {
  getAllServiceRequests,
  updateHqStatus,
  getPartnerList,
} from '../../../services/serviceRequestService';
import { assignPartner } from '../../../services/partnerAssignmentService';
import { MOVING_TYPE_LABELS } from '../../../services/customerFormService';
import { useAuth } from '../../../context/AuthContext';
import {
  ADMIN_REQUEST_FILTER_UNASSIGNED,
  readAdminRequestFilters,
  requestMatchesAdminCategory,
  requestMatchesAdminStatus,
  updateAdminRequestFilterParams,
} from '../../../utils/adminRequestFilters';

const CLEANING_TYPE_LABELS = {
  basic: '기본 입주청소',
  deep: '딥 클리닝',
};
const INTERNET_TYPE_LABELS = {
  internet_tv: '인터넷+TV',
  internet_only: '인터넷만',
  tv_only: 'TV만',
};
const PREFERRED_INTERNET_LABELS = {
  most_support: '최대 지원금',
  lowest_price: '최저가',
  kt: 'KT',
  skt: 'SKT',
  lg: 'LG U+',
};

const PER_PAGE = 20;

const getRequestCategory = (request) => (
  request?.category || (Array.isArray(request?.services) && request.services[0]) || ''
);

// 배정 업체 검색 콤보박스 — 업체명·담당자·연락처로 필터
const ComboWrap = styled.div`position: relative; width: 100%;`;
const ComboInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 13px;
  color: #1F2937;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &::placeholder { color: #9CA3AF; }
`;
const ComboList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
  max-height: 220px;
  overflow-y: auto;
`;
const ComboItem = styled.div`
  padding: 9px 12px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#EEF2FF' : '#fff')};
  &:hover { background: #F3F4F6; }
  & + & { border-top: 1px solid #F3F4F6; }
`;
const ComboItemName = styled.div`font-size: 13px; font-weight: 600; color: #1F2937;`;
const ComboItemSub = styled.div`font-size: 11px; color: #9CA3AF; margin-top: 2px;`;
const ComboEmpty = styled.div`padding: 12px; font-size: 12px; color: #9CA3AF; text-align: center;`;

function formatPartnerLabel(p) {
  if (!p) return '';
  const company = p.companyName || '';
  const manager = p.managerName || '';
  if (company && manager && company !== manager) return `${company} / ${manager}`;
  return company || manager || p.name || '';
}

function PartnerCombobox({ partners, value, onChange, placeholder, emptyText }) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const selected = partners.find((p) => p.id === value) || null;

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter((p) => {
      const hay = `${p.companyName || ''} ${p.name || ''} ${p.managerName || ''} ${p.phone || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [partners, keyword]);

  const pick = (p) => {
    onChange(p.id);
    setKeyword('');
    setOpen(false);
  };

  return (
    <ComboWrap>
      <ComboInput
        value={open ? keyword : formatPartnerLabel(selected)}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setKeyword(''); }}
        onBlur={() => { setTimeout(() => setOpen(false), 150); }}
        onChange={(e) => { setKeyword(e.target.value); setOpen(true); }}
      />
      {open && (
        <ComboList>
          {filtered.length === 0 ? (
            <ComboEmpty>{emptyText || '검색 결과가 없습니다'}</ComboEmpty>
          ) : (
            filtered.map((p) => (
              <ComboItem
                key={p.id}
                $active={p.id === value}
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              >
                <ComboItemName>{formatPartnerLabel(p)}</ComboItemName>
                {p.phone && (
                  <ComboItemSub>{p.phone}</ComboItemSub>
                )}
              </ComboItem>
            ))
          )}
        </ComboList>
      )}
    </ComboWrap>
  );
}

const RequestList = () => {
  const { userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => readAdminRequestFilters(searchParams).status);
  const [partnerFilter, setPartnerFilter] = useState(() => readAdminRequestFilters(searchParams).partner);
  const [categoryFilter, setCategoryFilter] = useState(() => readAdminRequestFilters(searchParams).category);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const next = readAdminRequestFilters(searchParams);
    setStatusFilter(next.status);
    setCategoryFilter(next.category);
    setPartnerFilter(next.partner);
    setPage(1);
  }, [searchParams]);

  const updateFilter = (key, value, setFilter) => {
    setFilter(value);
    setPage(1);
    setSearchParams(updateAdminRequestFilterParams(searchParams, key, value), { replace: true });
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [assignModal, setAssignModal] = useState(false);
  const [categoryPartnerMap, setCategoryPartnerMap] = useState({}); // { category: partner_id }
  const [activeCategory, setActiveCategory] = useState(''); // 카테고리 탭 활성

  const [detail, setDetail] = useState(null);
  const [bulkStatusModal, setBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [memoModal, setMemoModal] = useState(null); // { id, memo }
  const [showAllPartners, setShowAllPartners] = useState(false); // 카테고리 필터 우회 (escape hatch)
  const [expandTargetList, setExpandTargetList] = useState(false); // "목록 펼치기" 토글
  const [singleAssign, setSingleAssign] = useState(null); // 단건 배정 대상 row
  const [singleAssignPartner, setSingleAssignPartner] = useState(''); // 단건 배정 업체 id
  const [singleShowAll, setSingleShowAll] = useState(false); // 단건 배정 카테고리 필터 해제

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqs, parts] = await Promise.all([
        getAllServiceRequests({}),
        getPartnerList(),
      ]);
      setRequests(reqs);
      setPartners(parts);
    } catch (err) {
      console.error('서비스 요청 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return requests.filter((r) => {
      if (!requestMatchesAdminStatus(r, statusFilter)) return false;
      if (!requestMatchesAdminCategory(r, categoryFilter)) return false;
      if (partnerFilter !== 'all') {
        if (partnerFilter === 'none' && r.assigned_partner_id) return false;
        if (partnerFilter !== 'none' && r.assigned_partner_id !== partnerFilter) return false;
      }
      if (kw) {
        const haystack = `${r.customerName || ''}${r.phone || ''}${r.partnerName || ''}${r.realtorName || ''}${r.sourceUrlDisplay || ''}${r.firstReferralAuditRealtorName || ''}${r.firstReferralAuditStatusLabel || ''}`;
        if (!haystack.includes(kw)) return false;
      }
      return true;
    });
  }, [requests, keyword, statusFilter, categoryFilter, partnerFilter]);

  // 같은 신청(같은 고객/app_number) 끼리 묶기 — 좌측 customer 정보 + 우측 서비스별 sub-row
  const grouped = useMemo(() => {
    // 정렬용 timestamp 추출 — created_at(Firestore Timestamp) / createdAt(Timestamp 또는 'yyyy-MM-dd HH:mm' 문자열) 모두 대응
    const getSec = (r) => {
      if (r.created_at?.seconds) return r.created_at.seconds;
      if (r.createdAt?.seconds) return r.createdAt.seconds;
      if (typeof r.createdAt === 'string' && r.createdAt) {
        const t = Date.parse(r.createdAt.replace(' ', 'T') + '+09:00');
        if (!Number.isNaN(t)) return Math.floor(t / 1000);
      }
      return 0;
    };
    const map = new Map();
    filtered.forEach((r) => {
      // 키 우선순위: app_number > phone+name > customer_id > id
      const key = r.app_number
        || (r.phone && r.customerName ? `${r.phone}__${r.customerName}` : null)
        || r.customer_id
        || r.id;
      if (!map.has(key)) {
        map.set(key, {
          key,
          customerName: r.customerName,
          phone: r.phone,
          address: r.address,
          realtorName: r.realtorName,
          app_number: r.app_number || '',
          rows: [],
          latest: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      const sec = getSec(r);
      if (sec > g.latest) g.latest = sec;
    });
    // 각 그룹 내 sub-row 도 최근 신청 → 옛 신청 순으로 정렬
    map.forEach((g) => {
      g.rows.sort((a, b) => getSec(b) - getSec(a));
    });
    const arr = Array.from(map.values());
    arr.sort((a, b) => b.latest - a.latest);
    return arr;
  }, [filtered]);

  const pagedGroups = grouped.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(grouped.length / PER_PAGE));

  // KPI 계산
  const kpi = useMemo(() => {
    const unread = requests.filter((r) => r.hqStatus === 'unread').length;
    const unassigned = requests.filter((r) => !r.assigned_partner_id).length;
    const reserved = requests.filter((r) => ['assigned', 'settlement_check', 'settlement_done'].includes(r.hqStatus)).length;
    const cancelled = requests.filter((r) => r.hqStatus === 'cancelled' || r.hqStatus === 'unmatched_cancelled').length;
    return { unread, unassigned, reserved, cancelled };
  }, [requests]);

  // 지연 DB (배정 후 24시간 경과, 업체 상태 unread/read)
  const delayed = useMemo(() => {
    const now = Date.now();
    return requests.filter((r) => {
      if (!r.assigned_partner_id) return false;
      if (!['unread', 'read'].includes(r.partnerStatus)) return false;
      const at = r.assigned_at?.seconds ? r.assigned_at.seconds * 1000 : 0;
      if (!at) return false;
      return (now - at) >= 24 * 60 * 60 * 1000;
    }).slice(0, 8);
  }, [requests]);

  // 취소·미매칭 취소 건은 배정 대상 아님
  const isCancelled = (r) => r.hqStatus === 'cancelled' || r.hqStatus === 'unmatched_cancelled';
  // 배정된 row 또는 취소된 row 는 선택 불가 (중복 배정 방지 + 취소건 배정 차단)
  const isLocked = (r) => !!r.assigned_partner_id || isCancelled(r);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // 그룹 단위 선택/해제 (해당 그룹의 미배정 sub-row 만)
  const toggleSelectGroup = (g) => {
    const selectableIds = g.rows.filter((r) => !isLocked(r)).map((r) => r.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !selectableIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...selectableIds])));
    }
  };

  // 페이지 전체 선택 (현재 페이지 그룹들의 미배정 sub-row)
  const toggleSelectAll = () => {
    const selectableIds = pagedGroups.flatMap((g) => g.rows.filter((r) => !isLocked(r)).map((r) => r.id));
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !selectableIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...selectableIds])));
    }
  };

  // 선택된 row 의 정보 (id, customer, category)
  const selectedRows = useMemo(() => {
    return selectedIds
      .map((id) => requests.find((x) => x.id === id))
      .filter(Boolean);
  }, [selectedIds, requests]);

  // 선택된 SR 을 카테고리별로 그룹핑 → 탭 목록
  const selectedByCategory = useMemo(() => {
    const map = new Map();
    selectedRows.forEach((r) => {
      const cat = getRequestCategory(r);
      if (!cat) return;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(r);
    });
    return map; // Map<category, rows[]>
  }, [selectedRows]);

  // row 1개의 카테고리 처리 가능한 partner 만 노출 (행별 필터링)
  const partnersForCategory = (cat) => {
    if (!cat) return partners;
    return partners.filter((p) => {
      const cats = Array.isArray(p.service_categories) ? p.service_categories : [];
      return cats.includes(cat);
    });
  };

  const handleBulkAssign = () => {
    if (selectedIds.length === 0) { alert('배정할 요청을 선택하세요.'); return; }
    // 첫번째 카테고리 자동 활성
    const cats = Array.from(new Set(selectedIds
      .map((id) => requests.find((x) => x.id === id))
      .filter(Boolean)
      .map((r) => getRequestCategory(r))
      .filter(Boolean)));
    setCategoryPartnerMap({});
    setShowAllPartners(false);
    setExpandTargetList(false);
    setActiveCategory(cats[0] || '');
    setAssignModal(true);
  };

  // 활성 카테고리 한 묶음 → 같은 업체로 일괄 배정
  const confirmCategoryAssign = async () => {
    const cat = activeCategory;
    if (!cat) return;
    const rows = selectedByCategory.get(cat) || [];
    if (rows.length === 0) { alert('선택된 요청이 없습니다.'); return; }
    const pid = categoryPartnerMap[cat];
    if (!pid) { alert('배정할 업체를 선택하세요.'); return; }
    try {
      for (const r of rows) {
        await assignPartner(r.id, pid, userData?.uid);
      }
      const assignedIds = rows.map((r) => r.id);
      const restSelectedIds = selectedIds.filter((id) => !assignedIds.includes(id));
      // 남은 카테고리 자동 전환 (없으면 모달 닫기)
      const restCats = Array.from(new Set(restSelectedIds
        .map((id) => requests.find((x) => x.id === id))
        .filter(Boolean)
        .map((r) => getRequestCategory(r))
        .filter(Boolean)));
      setSelectedIds(restSelectedIds);
      setCategoryPartnerMap((prev) => {
        const { [cat]: _, ...rest } = prev;
        return rest;
      });
      setExpandTargetList(false);
      if (restCats.length === 0) {
        setAssignModal(false);
        setActiveCategory('');
      } else {
        setActiveCategory(restCats[0]);
      }
      fetchData();
    } catch (err) {
      console.error('일괄 배정 실패:', err);
      alert('배정 중 오류가 발생했습니다.');
    }
  };

  // 활성 카테고리 SR 모두 선택 해제 (탭 사라지고 다음 탭으로)
  const clearCategorySelection = () => {
    const cat = activeCategory;
    const rows = selectedByCategory.get(cat) || [];
    const ids = rows.map((r) => r.id);
    const rest = selectedIds.filter((id) => !ids.includes(id));
    setSelectedIds(rest);
    setExpandTargetList(false);
    const restCats = Array.from(new Set(rest
      .map((id) => requests.find((x) => x.id === id))
      .filter(Boolean)
      .map((r) => getRequestCategory(r))
      .filter(Boolean)));
    if (restCats.length === 0) {
      setAssignModal(false);
      setActiveCategory('');
    } else {
      setActiveCategory(restCats[0]);
    }
  };

  // 테이블 행에서 바로 단건 배정 — 팝업 열기
  const openSingleAssign = (r) => {
    setSingleAssign(r);
    setSingleAssignPartner('');
    setSingleShowAll(false);
  };

  // 단건 배정 확정
  const confirmSingleAssign = async () => {
    if (!singleAssign) return;
    if (!singleAssignPartner) { alert('배정할 업체를 선택하세요.'); return; }
    try {
      await assignPartner(singleAssign.id, singleAssignPartner, userData?.uid);
      setSingleAssign(null);
      setSingleAssignPartner('');
      fetchData();
    } catch (err) {
      console.error('단건 배정 실패:', err);
      alert('배정 중 오류가 발생했습니다.');
    }
  };

  const handleBulkUnassign = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.length}건의 배정을 해제할까요?`)) return;
    try {
      for (const id of selectedIds) {
        await updateHqStatus(id, 'read', { assigned_partner_id: null });
      }
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      console.error('배정 해제 실패:', err);
    }
  };

  // 일괄 상태변경
  const handleBulkStatusChange = async () => {
    if (!bulkStatusValue) { alert('변경할 상태를 선택하세요.'); return; }
    if (selectedIds.length === 0) return;
    try {
      for (const id of selectedIds) {
        await updateHqStatus(id, bulkStatusValue);
      }
      setSelectedIds([]);
      setBulkStatusModal(false);
      setBulkStatusValue('');
      fetchData();
    } catch (err) {
      console.error('일괄 상태변경 실패:', err);
      alert('상태변경 중 오류 발생');
    }
  };

  // 메모 저장
  const handleMemoSave = async () => {
    if (!memoModal) return;
    try {
      await updateDoc(doc(db, 'service_requests', memoModal.id), {
        admin_memo: memoModal.memo || '',
        updated_at: serverTimestamp(),
      });
      setMemoModal(null);
      fetchData();
    } catch (err) {
      console.error('메모 저장 실패:', err);
      alert('저장 실패');
    }
  };

  const handleExport = () => {
    const rows = [
      ['순번', '고객명', '연락처', '유입부동산', '업체', '상담요청', '신청일', '희망일', '본사상태', '배정일', '배정업체'],
      ...filtered.map((r, idx) => [
        idx + 1,
        r.customerName || '',
        r.phone || '',
        r.realtorName && r.realtorName !== '-' ? r.realtorName : '',
        r.partnerName || '',
        r.categoryLabel || SERVICE_CATEGORIES[r.category]?.label || '',
        r.createdAt || '',
        r.desiredDate || r.movingDate || r.moving_date || '',
        HQ_STATUS[r.hqStatus]?.label || '',
        r.assigned_at?.seconds ? new Date(r.assigned_at.seconds * 1000).toLocaleDateString('ko-KR') : '',
        r.partnerName || '',
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service_requests_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const elapsedLabel = (ts) => {
    if (!ts?.seconds) return '';
    const diff = Date.now() - ts.seconds * 1000;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 48) return `${hours}시간 경과`;
    const days = Math.floor(hours / 24);
    return `${days}일 경과`;
  };

  const StatusText = ({ status }) => {
    const s = HQ_STATUS[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>;
  };

  return (
    <Container>
      <Title>전체 서비스 요청</Title>

      <KpiRow>
        <KpiCard><KpiLabel>신규 (미확인)</KpiLabel><KpiValue $color="#EF4444">{kpi.unread}</KpiValue></KpiCard>
        <KpiCard><KpiLabel>미배정 DB</KpiLabel><KpiValue $color="#F59E0B">{kpi.unassigned}</KpiValue></KpiCard>
        <KpiCard><KpiLabel>예약 / 계약</KpiLabel><KpiValue $color="#22C55E">{kpi.reserved}</KpiValue></KpiCard>
        <KpiCard><KpiLabel>실패 / 취소</KpiLabel><KpiValue $color="#6B7280">{kpi.cancelled}</KpiValue></KpiCard>
      </KpiRow>

      <NoticeBox>
        미확인 보기 + 필터 결과 전체 선택 → 일괄 배정 클릭 시 동일 항목 전체 배정이 가능합니다.
      </NoticeBox>

      <ActionBar>
        <SelectCount>선택 {selectedIds.length}건</SelectCount>
        <ActionBtn onClick={handleBulkAssign}>일괄 배정</ActionBtn>
        <ActionBtn onClick={handleBulkUnassign}>배정 해제</ActionBtn>
        <ActionBtn onClick={() => { if (selectedIds.length === 0) { alert('변경할 요청을 선택하세요.'); return; } setBulkStatusModal(true); }}>일괄 상태변경</ActionBtn>
        <ActionBtn onClick={handleExport}>엑셀 다운로드</ActionBtn>
      </ActionBar>

      <FilterBar>
        <SearchInput
          placeholder="고객명 / 연락처 / 배정업체명 검색"
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
        />
        <Select
          aria-label="서비스 카테고리 필터"
          value={categoryFilter}
          onChange={(e) => updateFilter('category', e.target.value, setCategoryFilter)}
        >
          <option value="all">전체 카테고리</option>
          {Object.entries(SERVICE_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select
          aria-label="본사 상태 필터"
          value={statusFilter}
          onChange={(e) => updateFilter('status', e.target.value, setStatusFilter)}
        >
          <option value="all">본사상태 (전체)</option>
          <option value={ADMIN_REQUEST_FILTER_UNASSIGNED}>미배정 상담 (미확인/확인)</option>
          {Object.entries(HQ_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select
          aria-label="배정업체 필터"
          value={partnerFilter}
          onChange={(e) => updateFilter('partner', e.target.value, setPartnerFilter)}
        >
          <option value="all">배정업체 (전체)</option>
          <option value="none">미배정</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{formatPartnerLabel(p) || p.name}</option>
          ))}
        </Select>
      </FilterBar>

      {delayed.length > 0 && (
        <DelayedSection>
          <DelayedTitle>지연 DB <DelayedCount>({delayed.length}건)</DelayedCount></DelayedTitle>
          <DelayedGrid>
            {delayed.map((r) => (
              <DelayedCard key={r.id}>
                <DelayedRow>
                  <DelayedCat>{r.categoryLabel || SERVICE_CATEGORIES[r.category]?.label || ''}</DelayedCat>
                  <DelayedElapsed>{elapsedLabel(r.assigned_at)}</DelayedElapsed>
                </DelayedRow>
                <DelayedName>{r.customerName}</DelayedName>
                <DelayedSub>배정: {r.partnerName || '-'}</DelayedSub>
                <UnassignLink onClick={async () => {
                  if (!window.confirm('이 배정을 해제할까요?')) return;
                  await updateHqStatus(r.id, 'read', { assigned_partner_id: null });
                  fetchData();
                }}>배정 취소</UnassignLink>
              </DelayedCard>
            ))}
          </DelayedGrid>
        </DelayedSection>
      )}

      <MobileCardList aria-label="서비스 요청 카드 목록">
        {loading ? <MobileListState>로딩 중...</MobileListState> : pagedGroups.length === 0 ? <MobileListState>조건에 해당하는 요청이 없습니다.</MobileListState> : pagedGroups.map((group, groupIndex) => {
          const selectableIds = group.rows.filter((request) => !isLocked(request)).map((request) => request.id);
          const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
          return (
            <RequestGroupCard key={group.key} $borderTone={groupIndex % 2 === 0 ? 'dark' : 'light'}>
              <MobileCardHeader>
                <MobileCardHeading>
                  <MobileCardTitle>{group.customerName || '-'}</MobileCardTitle>
                  <MobileCardSubtitle>{group.realtorName && group.realtorName !== '-' ? `${group.realtorName} · ` : ''}신청 {group.rows.length}건</MobileCardSubtitle>
                </MobileCardHeading>
              </MobileCardHeader>
              <MobileCardGrid>
                <MobileCardField><MobileCardLabel>연락처</MobileCardLabel><MobileCardValue>{group.phone || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField $wide><MobileCardLabel>주소</MobileCardLabel><MobileCardValue>{group.address || '-'}</MobileCardValue></MobileCardField>
              </MobileCardGrid>
              <MobileGroupSelect>
                <MobileCardSelect>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => toggleSelectGroup(group)}
                    disabled={selectableIds.length === 0}
                  />
                  배정 가능한 요청 전체 선택
                </MobileCardSelect>
              </MobileGroupSelect>
              <MobileRequestStack>
                {group.rows.map((request) => {
                  const locked = isLocked(request);
                  const category = getRequestCategory(request);
                  const categoryConfig = SERVICE_CATEGORIES[category];
                  const categoryLabel = categoryConfig?.label || category || '-';
                  return (
                    <MobileRequestItem
                      key={request.id}
                      $categoryColor={categoryConfig?.color}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetail(request)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setDetail(request);
                        }
                      }}
                    >
                      <MobileRequestHeader>
                        <CategoryBadge $categoryColor={categoryConfig?.color}>{categoryLabel}</CategoryBadge>
                        <StatusText status={request.hqStatus} />
                      </MobileRequestHeader>
                      <MobileRequestMeta>
                        <div><span>배정 업체</span><strong>{locked ? (request.partnerName || '-') : '미배정'}</strong></div>
                        <div><span>접수일</span><strong>{request.createdAt || '-'}</strong></div>
                        <div className="wide"><span>관리자 메모</span><strong>{request.admin_memo || '메모 없음'}</strong></div>
                      </MobileRequestMeta>
                      <MobileRequestActions onClick={(event) => event.stopPropagation()}>
                        <MobileCardSelect>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(request.id)}
                            onChange={() => toggleSelect(request.id)}
                            disabled={locked}
                          />
                          선택
                        </MobileCardSelect>
                        {!locked && <RowAssignBtn onClick={() => openSingleAssign(request)}>배정하기</RowAssignBtn>}
                        <MobileMemoButton type="button" onClick={() => setMemoModal({ id: request.id, memo: request.admin_memo || '' })}>{request.admin_memo ? '메모 수정' : '메모 추가'}</MobileMemoButton>
                      </MobileRequestActions>
                    </MobileRequestItem>
                  );
                })}
              </MobileRequestStack>
            </RequestGroupCard>
          );
        })}
      </MobileCardList>

      <DesktopListOnly>
      <GroupTable>
        <colgroup>
          <col style={{ width: 220 }} />
          <col style={{ width: 36 }} />
          <col style={{ width: 130 }} />
          <col />
          <col style={{ width: 110 }} />
          <col style={{ width: 90 }} />
          <col />
          <col style={{ width: 130 }} />
          <col style={{ width: 80 }} />
        </colgroup>
        <thead>
          <tr>
            <Th>고객</Th>
            <Th>
              <input
                type="checkbox"
                checked={(() => {
                  const all = pagedGroups.flatMap((g) => g.rows.filter((r) => !isLocked(r)).map((r) => r.id));
                  return all.length > 0 && all.every((id) => selectedIds.includes(id));
                })()}
                onChange={toggleSelectAll}
              />
            </Th>
            <Th>연락처</Th>
            <Th>주소</Th>
            <Th>요청 서비스</Th>
            <Th>진행 상태</Th>
            <Th>배정 / 판매 업체</Th>
            <Th>접수일</Th>
            <Th>메모</Th>
          </tr>
        </thead>

        {loading ? (
          <tbody><tr><Td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>로딩 중...</Td></tr></tbody>
        ) : pagedGroups.length === 0 ? (
          <tbody><tr><Td colSpan={9} style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>조건에 해당하는 요청이 없습니다.</Td></tr></tbody>
        ) : pagedGroups.map((g) => {
          const groupSelectableIds = g.rows.filter((r) => !isLocked(r)).map((r) => r.id);
          const groupAllSelected = groupSelectableIds.length > 0 && groupSelectableIds.every((id) => selectedIds.includes(id));
          const rowspan = g.rows.length;
          return (
            <GroupTbody key={g.key}>
              {g.rows.map((r, idx) => {
                const locked = isLocked(r);
                const catLabel = SERVICE_CATEGORIES[r.category]?.label || r.category || '-';
                return (
                  <GroupRow key={r.id} $first={idx === 0} onClick={() => setDetail(r)}>
                    {idx === 0 && (
                      <SideCell rowSpan={rowspan} onClick={(e) => e.stopPropagation()}>
                        <SideCellInner>
                          <GroupSideName>{g.customerName || '-'}</GroupSideName>
                          {g.realtorName && g.realtorName !== '-' && (
                            <GroupSideMeta>{g.realtorName}</GroupSideMeta>
                          )}
                          <GroupSideCount>신청 {rowspan}건</GroupSideCount>
                          <GroupSideAll>
                            <input
                              type="checkbox"
                              checked={groupAllSelected}
                              onChange={() => toggleSelectGroup(g)}
                              disabled={groupSelectableIds.length === 0}
                              title={groupSelectableIds.length === 0 ? '미배정 신청이 없습니다.' : '그룹 전체 선택'}
                            />
                            <span>그룹 선택</span>
                          </GroupSideAll>
                        </SideCellInner>
                      </SideCell>
                    )}
                    <Td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        disabled={locked}
                        title={isCancelled(r) ? '취소된 신청은 배정할 수 없습니다.' : (locked ? '이미 배정된 신청은 다시 선택할 수 없습니다.' : '')}
                      />
                    </Td>
                    {idx === 0 && (
                      <MergedTd rowSpan={rowspan}>{g.phone || '-'}</MergedTd>
                    )}
                    {idx === 0 && (
                      <MergedTd rowSpan={rowspan} title={g.address || ''}>{g.address || '-'}</MergedTd>
                    )}
                    <Td><strong>{catLabel}</strong></Td>
                    <Td><StatusText status={r.hqStatus} /></Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      {locked
                        ? (r.partnerName || '-')
                        : <RowAssignBtn onClick={() => openSingleAssign(r)}>+ 배정하기</RowAssignBtn>}
                    </Td>
                    <Td>{r.createdAt || '-'}</Td>
                    <Td onClick={(e) => { e.stopPropagation(); setMemoModal({ id: r.id, memo: r.admin_memo || '' }); }}>
                      {r.admin_memo ? <MemoPreview title={r.admin_memo}>{r.admin_memo}</MemoPreview> : <MemoAdd>+ 메모</MemoAdd>}
                    </Td>
                  </GroupRow>
                );
              })}
            </GroupTbody>
          );
        })}
      </GroupTable>
      </DesktopListOnly>

      <PageFoot>
        <PageInfo>페이지 {page} / {totalPages} · 총 {filtered.length}건</PageInfo>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </PageFoot>

      {assignModal && (() => {
        const cats = Array.from(selectedByCategory.keys());
        const activeRows = selectedByCategory.get(activeCategory) || [];
        const activeLabel = SERVICE_CATEGORIES[activeCategory]?.label || activeCategory || '-';
        const partnerList = showAllPartners ? partners : partnersForCategory(activeCategory);
        const selectedPartnerId = categoryPartnerMap[activeCategory] || '';
        return (
          <ModalOverlay onClick={() => setAssignModal(false)}>
            <AssignModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <div>
                  <ModalTitle>DB 배정 상세</ModalTitle>
                  <ModalSubtitle>선택된 요청을 배정할 업체를 지정합니다.</ModalSubtitle>
                </div>
                <CloseBtn onClick={() => setAssignModal(false)}><IoClose size={22} /></CloseBtn>
              </ModalHeader>
              <ModalBody>
                {cats.length > 1 && (
                  <CategoryTabs>
                    {cats.map((c) => {
                      const label = SERVICE_CATEGORIES[c]?.label || c;
                      const count = (selectedByCategory.get(c) || []).length;
                      const active = c === activeCategory;
                      return (
                        <CategoryTab key={c} $active={active} onClick={() => { setActiveCategory(c); setExpandTargetList(false); }}>
                          <span>{label}</span>
                          <CategoryTabCount $active={active}>{count}</CategoryTabCount>
                        </CategoryTab>
                      );
                    })}
                  </CategoryTabs>
                )}

                {activeCategory && (
                  <AssignBox>
                    <AssignBoxHeader>
                      <AssignBoxHeaderLeft>
                        <ActiveCatTitleChip>{activeLabel}</ActiveCatTitleChip>
                        <ActiveCatTitle>{activeLabel}</ActiveCatTitle>
                      </AssignBoxHeaderLeft>
                      <CandidatePill>후보 업체 {partnerList.length}곳</CandidatePill>
                    </AssignBoxHeader>
                    <AssignBoxSubLine>배정 가능 {activeRows.length}건 · 선택 {activeRows.length}건</AssignBoxSubLine>

                    <AssignFormRow>
                      <AssignFormCol>
                        {partnerList.length === 0 ? (
                          <AssignRowEmpty>처리 가능한 업체 없음</AssignRowEmpty>
                        ) : (
                          <PartnerCombobox
                            partners={partnerList}
                            value={selectedPartnerId}
                            onChange={(id) => setCategoryPartnerMap((prev) => ({ ...prev, [activeCategory]: id }))}
                            placeholder="업체명, 담당자, 연락처 검색"
                            emptyText="검색 결과가 없습니다"
                          />
                        )}
                      </AssignFormCol>
                      <AssignQtyBox>
                        <AssignQtyLabel>배정 수량</AssignQtyLabel>
                        <AssignQtyValue>{activeRows.length}</AssignQtyValue>
                      </AssignQtyBox>
                    </AssignFormRow>

                    <SelectedReqHead>
                      <SelectedReqTitle>선택된 요청 <CountPill>{activeRows.length}건</CountPill></SelectedReqTitle>
                      <SelectedReqActions>
                        <OutlineBtn onClick={() => alert('현재 카테고리 요청이 모두 선택되어 있습니다.')}>전체 선택</OutlineBtn>
                        <OutlineBtn onClick={clearCategorySelection}>선택 해제</OutlineBtn>
                        <OutlineBtn onClick={() => setExpandTargetList((v) => !v)}>
                          {expandTargetList ? '목록 접기' : '목록 펼치기'}
                        </OutlineBtn>
                      </SelectedReqActions>
                    </SelectedReqHead>

                    <DashedHint>선택된 요청이 {activeRows.length}건입니다. 배정할 업체를 선택하세요.</DashedHint>

                    <TargetListBox>
                      <TargetListInline>
                        <TargetListLabel>배정 대상 요청</TargetListLabel>
                        <TargetInlineText>
                          {(expandTargetList ? activeRows : activeRows.slice(0, 1)).map((r, idx, arr) => (
                            <span key={r.id}>
                              {r.customerName || '-'}{r.createdAt ? ` · ${String(r.createdAt).slice(0, 10)}` : ''}
                              {idx < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                          {!expandTargetList && activeRows.length > 1 && (
                            <TargetInlineMore> 외 {activeRows.length - 1}건</TargetInlineMore>
                          )}
                        </TargetInlineText>
                      </TargetListInline>
                    </TargetListBox>

                    <ToggleRow>
                      <input
                        id="show-all-partners"
                        type="checkbox"
                        checked={showAllPartners}
                        onChange={(e) => {
                          setShowAllPartners(e.target.checked);
                          setCategoryPartnerMap((prev) => ({ ...prev, [activeCategory]: '' }));
                        }}
                      />
                      <label htmlFor="show-all-partners">전체 업체 보기 (카테고리 필터 해제)</label>
                    </ToggleRow>
                  </AssignBox>
                )}
              </ModalBody>
              <ModalFooter>
                <GhostBtn onClick={() => setAssignModal(false)}>닫기</GhostBtn>
                <PrimaryBtn onClick={confirmCategoryAssign}>배정하기</PrimaryBtn>
              </ModalFooter>
            </AssignModalContent>
          </ModalOverlay>
        );
      })()}

      {detail && (() => {
        const detailCategory = getRequestCategory(detail);
        const detailCategoryConfig = SERVICE_CATEGORIES[detailCategory];
        return (
        <ModalOverlay onClick={() => setDetail(null)}>
          <RequestDetailModalContent $categoryColor={detailCategoryConfig?.color} onClick={(e) => e.stopPropagation()}>
            <RequestDetailHeader $categoryColor={detailCategoryConfig?.color}>
              <RequestDetailHeading>
                <CategoryBadge $categoryColor={detailCategoryConfig?.color}>
                  {detailCategoryConfig?.label || detailCategory || '신청 서비스'}
                </CategoryBadge>
                <div>
                  <ModalTitle>{detail.customerName || '고객 상세'}</ModalTitle>
                  <ModalSubtitle>신청내역 상세{detail.app_number ? ` · ${detail.app_number}` : ''}</ModalSubtitle>
                </div>
              </RequestDetailHeading>
              <CloseBtn onClick={() => setDetail(null)}><IoClose size={22} /></CloseBtn>
            </RequestDetailHeader>
            <ModalBody>
              <DetailSectionTitle>기본 정보</DetailSectionTitle>
              <DetailGrid>
                {detail.app_number && (
                  <DetailItem><DetailLabel>접수번호</DetailLabel><DetailValue>{detail.app_number}</DetailValue></DetailItem>
                )}
                <DetailItem><DetailLabel>고객명</DetailLabel><DetailValue>{detail.customerName}</DetailValue></DetailItem>
                <DetailItem><DetailLabel>연락처</DetailLabel><DetailValue>{detail.phone || '-'}</DetailValue></DetailItem>
                <DetailItem><DetailLabel>신청일</DetailLabel><DetailValue>{detail.createdAt}</DetailValue></DetailItem>
                <DetailItem><DetailLabel>유입부동산</DetailLabel><DetailValue>{detail.realtorName}</DetailValue></DetailItem>
                <DetailItem><DetailLabel>본사 상태</DetailLabel><DetailValue>{HQ_STATUS[detail.hqStatus]?.label || '-'}</DetailValue></DetailItem>
                <DetailItem><DetailLabel>배정 업체</DetailLabel><DetailValue>{detail.partnerName || '미배정'}</DetailValue></DetailItem>
              </DetailGrid>

              {detail.firstReferralAuditStatus && detail.firstReferralAuditStatus !== 'none' && (
                <>
                  <DetailSectionTitle>추천 감사 기록</DetailSectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>최초 추천인</DetailLabel>
                      <DetailValue>{detail.firstReferralAuditRealtorName || '기록 확인 필요'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>최초 유입시각</DetailLabel>
                      <DetailValue>{detail.firstReferralAuditCapturedAt || '기록 확인 필요'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>감사 판정</DetailLabel>
                      <DetailValue>{detail.firstReferralAuditStatusLabel || '기록 확인 필요'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>최초 기록 경로</DetailLabel>
                      <DetailValue>{detail.firstReferralAuditCaptureSourceLabel || '기록 확인 필요'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </>
              )}

              <DetailSectionTitle>이사 정보</DetailSectionTitle>
              <DetailGrid>
                {(() => {
                  const cat = detail.category || (Array.isArray(detail.services) ? detail.services[0] : '');
                  const isCleaning = cat === 'cleaning';
                  // customer_requested_date(일정변경 다이얼) 최우선 — 입주청소/인터넷 등 다이얼로 넣은 희망일이 '-'로 뜨던 버그
                  const dateVal = detail.customer_requested_date || detail.customerRequestedDate
                    || (isCleaning
                      ? (detail.cleaning_date || detail.cleaningDate)
                      : (detail.moving_date || detail.movingDate
                        || detail.cleaning_date || detail.cleaningDate
                        || detail.interior_date || detail.interiorDate
                        || detail.preferred_date || detail.preferredDate));
                  return (
                    <DetailItem>
                      <DetailLabel>{isCleaning ? '입주청소 희망일' : '서비스 희망일'}</DetailLabel>
                      <DetailValue>{dateVal || '-'}</DetailValue>
                    </DetailItem>
                  );
                })()}
                <DetailItem><DetailLabel>평수</DetailLabel><DetailValue>{detail.areaSize || '-'}</DetailValue></DetailItem>
                <DetailItem $full>
                  <DetailLabel>이사지 주소</DetailLabel>
                  <DetailValue>{detail.moving_address || detail.movingAddress || detail.address || detail.toAddress || detail.to_address || '-'}</DetailValue>
                </DetailItem>
              </DetailGrid>

              <DetailSectionTitle>신청 서비스</DetailSectionTitle>
              <DetailGrid>
                <DetailItem $full>
                  <DetailLabel>항목</DetailLabel>
                  <DetailServiceBadges>{(() => {
                    const arr = Array.isArray(detail.services) ? detail.services : (detail.category ? [detail.category] : []);
                    if (arr.length === 0) return '-';
                    return arr.map((k) => (
                      <CategoryBadge key={k} $categoryColor={SERVICE_CATEGORIES[k]?.color}>
                        {SERVICE_CATEGORIES[k]?.label || k}
                      </CategoryBadge>
                    ));
                  })()}</DetailServiceBadges>
                </DetailItem>

                {/* 이사 세부 */}
                {detail.moving_type && (
                  <DetailItem>
                    <DetailLabel>이사 유형</DetailLabel>
                    <DetailValue>{MOVING_TYPE_LABELS[detail.moving_type] || detail.moving_type}</DetailValue>
                  </DetailItem>
                )}

                {/* 청소 세부 */}
                {detail.cleaning_type && (
                  <DetailItem>
                    <DetailLabel>청소 유형</DetailLabel>
                    <DetailValue>{CLEANING_TYPE_LABELS[detail.cleaning_type] || detail.cleaning_type}</DetailValue>
                  </DetailItem>
                )}

                {/* 인터넷 세부 */}
                {detail.internet_type && (
                  <DetailItem>
                    <DetailLabel>인터넷 유형</DetailLabel>
                    <DetailValue>{INTERNET_TYPE_LABELS[detail.internet_type] || detail.internet_type}</DetailValue>
                  </DetailItem>
                )}
                {(detail.current_internet || detail.current_internet_provider) && (
                  <DetailItem>
                    <DetailLabel>기존 통신사</DetailLabel>
                    <DetailValue>{detail.current_internet || detail.current_internet_provider}</DetailValue>
                  </DetailItem>
                )}
                {detail.preferred_internet && (
                  <DetailItem>
                    <DetailLabel>선호 조건</DetailLabel>
                    <DetailValue>{PREFERRED_INTERNET_LABELS[detail.preferred_internet] || detail.preferred_internet}</DetailValue>
                  </DetailItem>
                )}

                {/* 인테리어 세부 */}
                {detail.interior_date && (
                  <DetailItem>
                    <DetailLabel>인테리어 희망일</DetailLabel>
                    <DetailValue>{detail.interior_date}</DetailValue>
                  </DetailItem>
                )}
                {detail.interior_style && (
                  <DetailItem>
                    <DetailLabel>인테리어 스타일</DetailLabel>
                    <DetailValue>{detail.interior_style}{detail.interior_custom ? ` (${detail.interior_custom})` : ''}</DetailValue>
                  </DetailItem>
                )}
              </DetailGrid>

              {detail.memo && (
                <>
                  <DetailSectionTitle>요청사항</DetailSectionTitle>
                  <MemoBox>{detail.memo}</MemoBox>
                </>
              )}

            </ModalBody>
            <ModalFooter>
              <PrimaryBtn onClick={() => setDetail(null)}>닫기</PrimaryBtn>
            </ModalFooter>
          </RequestDetailModalContent>
        </ModalOverlay>
        );
      })()}

      {/* 단건 배정 모달 — 테이블 행에서 바로 배정 */}
      {singleAssign && (() => {
        const cat = getRequestCategory(singleAssign);
        const catLabel = SERVICE_CATEGORIES[cat]?.label || cat || '-';
        const list = singleShowAll ? partners : partnersForCategory(cat);
        return (
          <ModalOverlay onClick={() => setSingleAssign(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>제휴업체 배정</ModalTitle>
                <CloseBtn onClick={() => setSingleAssign(null)}><IoClose size={22} /></CloseBtn>
              </ModalHeader>
              <ModalBody>
                <FieldLabel>대상</FieldLabel>
                <FieldValue>{singleAssign.customerName || '-'} · {catLabel}</FieldValue>

                <FieldLabel style={{ marginTop: 16 }}>
                  배정할 업체
                  {!singleShowAll && <FilterNote> · {catLabel} 처리 가능 업체만 표시</FilterNote>}
                </FieldLabel>
                {list.length === 0 ? (
                  <AssignRowEmpty style={{ marginTop: 4 }}>처리 가능한 업체가 없습니다. 전체 업체 보기를 켜세요.</AssignRowEmpty>
                ) : (
                  <div style={{ marginTop: 4 }}>
                    <PartnerCombobox
                      partners={list}
                      value={singleAssignPartner}
                      onChange={(id) => setSingleAssignPartner(id)}
                      placeholder="업체명, 담당자, 연락처 검색"
                      emptyText="검색 결과가 없습니다"
                    />
                  </div>
                )}

                <ToggleRow>
                  <input
                    id="single-show-all"
                    type="checkbox"
                    checked={singleShowAll}
                    onChange={(e) => { setSingleShowAll(e.target.checked); setSingleAssignPartner(''); }}
                  />
                  <label htmlFor="single-show-all">전체 업체 보기 (카테고리 필터 해제)</label>
                </ToggleRow>
              </ModalBody>
              <ModalFooter>
                <GhostBtn onClick={() => setSingleAssign(null)}>취소</GhostBtn>
                <PrimaryBtn onClick={confirmSingleAssign}>배정하기</PrimaryBtn>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        );
      })()}

      {/* 일괄 상태변경 모달 */}
      {bulkStatusModal && (
        <ModalOverlay onClick={() => setBulkStatusModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>일괄 상태변경</ModalTitle>
              <CloseBtn onClick={() => setBulkStatusModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>대상</FieldLabel>
              <FieldValue>선택한 {selectedIds.length}건</FieldValue>
              <FieldLabel style={{ marginTop: 16 }}>변경할 상태</FieldLabel>
              <Select value={bulkStatusValue} onChange={(e) => setBulkStatusValue(e.target.value)}>
                <option value="">상태를 선택하세요</option>
                {Object.entries(HQ_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Select>
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => setBulkStatusModal(false)}>취소</GhostBtn>
              <PrimaryBtn onClick={handleBulkStatusChange}>변경하기</PrimaryBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 메모 편집 모달 */}
      {memoModal && (
        <ModalOverlay onClick={() => setMemoModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>관리자 메모</ModalTitle>
              <CloseBtn onClick={() => setMemoModal(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <MemoTextarea
                value={memoModal.memo}
                onChange={(e) => setMemoModal({ ...memoModal, memo: e.target.value })}
                placeholder="관리자 내부 메모 (고객에게 노출되지 않음)"
                autoFocus
              />
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => setMemoModal(null)}>취소</GhostBtn>
              <PrimaryBtn onClick={handleMemoSave}>저장</PrimaryBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default RequestList;

// --- Styled ---
const Container = styled.div`width:100%;min-width:0;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin-bottom:20px;@media(max-width:768px){margin-bottom:14px;}`;

const KpiRow = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px;@media(max-width:768px){grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}`;
const KpiCard = styled.div`background:#fff;padding:18px 20px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};@media(max-width:768px){padding:13px 14px;}`;
const KpiLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const KpiValue = styled.div`font-size:22px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;

const NoticeBox = styled.div`
  background:#FFFBEB;
  color:#713F12;
  border:1px solid #FDE68A;
  border-radius:${({theme})=>theme.radius.sm};
  padding:10px 14px;
  font-size:13px;
  margin-bottom:16px;
  @media(max-width:768px){padding:10px 12px;font-size:12px;line-height:1.5;}
`;

const ActionBar = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap;@media(max-width:768px){display:grid;grid-template-columns:repeat(2,minmax(0,1fr));}`;
const SelectCount = styled.span`font-size:13px;color:${({theme})=>theme.colors.textSecondary};margin-right:4px;@media(max-width:768px){grid-column:1/-1;}`;
const ActionBtn = styled.button`
  padding:7px 14px;
  border:1px solid ${({ $danger, theme }) => ($danger ? '#EF4444' : theme.colors.border)};
  border-radius:${({theme})=>theme.radius.sm};
  background:#fff;
  font-size:13px;
  color:${({ $danger, theme }) => ($danger ? '#EF4444' : theme.colors.text)};
  cursor:pointer;
  &:hover{background:${({ $danger }) => ($danger ? '#FEF2F2' : '#F9FAFB')};}
  @media(max-width:768px){min-height:44px;padding:8px 10px;}
`;

const MemoPreview = styled.span`
  display:inline-block;
  max-width:140px;
  color:#374151;
  font-size:12px;
  cursor:pointer;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  vertical-align:bottom;
  &:hover{text-decoration:underline;}
`;
const MemoAdd = styled.span`
  color:#9CA3AF;
  font-size:12px;
  cursor:pointer;
  &:hover{color:${({theme})=>theme.colors.primary};}
`;
const MemoTextarea = styled.textarea`
  width:100%;
  min-height:140px;
  padding:12px;
  border:1px solid ${({theme})=>theme.colors.border};
  border-radius:${({theme})=>theme.radius.sm};
  font-size:13px;
  font-family:inherit;
  resize:vertical;
  &:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}
`;

const FilterBar = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:16px;flex-wrap:wrap;@media(max-width:768px){display:grid;grid-template-columns:repeat(2,minmax(0,1fr));align-items:stretch;>select:last-child{grid-column:1/-1;}}`;
const SearchInput = styled.input`
  flex:1;min-width:220px;
  padding:8px 12px;
  border:1px solid ${({theme})=>theme.colors.border};
  border-radius:${({theme})=>theme.radius.sm};
  font-size:13px;
  &:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}
  @media(max-width:768px){grid-column:1/-1;width:100%;min-width:0;min-height:44px;}
`;
const Select = styled.select`
  padding:8px 12px;
  border:1px solid ${({theme})=>theme.colors.border};
  border-radius:${({theme})=>theme.radius.sm};
  font-size:13px;
  background:#fff;
  &:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}
  @media(max-width:768px){width:100%;min-width:0;min-height:44px;}
`;

const DelayedSection = styled.div`
  background:#fff;
  border:1px solid ${({theme})=>theme.colors.borderLight};
  border-radius:${({theme})=>theme.radius.md};
  padding:16px 18px;
  margin-bottom:16px;
  @media(max-width:768px){padding:14px;}
`;
const DelayedTitle = styled.h3`font-size:14px;font-weight:600;margin:0 0 12px;color:#1F2937;`;
const DelayedCount = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};font-weight:500;margin-left:4px;`;
const DelayedGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px;`;
const DelayedCard = styled.div`
  background:#FEF2F2;
  border:1px solid #FECACA;
  border-radius:${({theme})=>theme.radius.sm};
  padding:12px 14px;
`;
const DelayedRow = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;`;
const DelayedCat = styled.span`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
const DelayedElapsed = styled.span`font-size:12px;color:#EF4444;font-weight:600;`;
const DelayedName = styled.div`font-size:14px;font-weight:600;color:#1F2937;margin-bottom:2px;`;
const DelayedSub = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const UnassignLink = styled.button`
  background:none;border:none;
  padding:0;
  font-size:12px;color:#6B7280;
  text-decoration:underline;
  cursor:pointer;
  &:hover{color:#1F2937;}
`;

const GroupTable = styled.table`
  width:100%;
  border-collapse:separate;
  border-spacing:0 10px;
  background:transparent;
  thead tr{background:#F9FAFB;}
  thead th{
    padding:10px 14px;
    text-align:left;
    font-size:12px;font-weight:500;
    color:${({theme})=>theme.colors.textSecondary};
    border-top:1px solid ${({theme})=>theme.colors.border};
    border-bottom:1px solid ${({theme})=>theme.colors.border};
    background:#F9FAFB;
  }
  thead th:first-child{border-left:1px solid ${({theme})=>theme.colors.border};border-top-left-radius:${({theme})=>theme.radius.md};border-bottom-left-radius:${({theme})=>theme.radius.md};}
  thead th:last-child{border-right:1px solid ${({theme})=>theme.colors.border};border-top-right-radius:${({theme})=>theme.radius.md};border-bottom-right-radius:${({theme})=>theme.radius.md};}
`;

const RequestGroupCard = styled(MobileListCard)`
  border-width: 2px;
  border-color: ${({$borderTone,theme})=>$borderTone === 'dark' ? theme.colors.text : theme.colors.borderStrong};
`;
const MobileGroupSelect = styled.div`
  padding: 0 14px 10px;
  border-bottom: 1px solid ${({theme})=>theme.colors.borderLight};
`;
const MobileRequestStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: ${({theme})=>theme.colors.borderLight};
`;
const MobileRequestItem = styled.section`
  overflow: hidden;
  border: 1px solid ${({$categoryColor,theme})=>$categoryColor || theme.colors.border};
  border-left-width: 4px;
  border-radius: ${({theme})=>theme.radius.sm};
  background: ${({theme})=>theme.colors.bgWhite};
  background: ${({$categoryColor,theme})=>`color-mix(in srgb, ${$categoryColor || theme.colors.primary} 7%, ${theme.colors.bgWhite})`};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({theme})=>theme.colors.primary};
    outline-offset: 1px;
  }
`;
const MobileRequestHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 12px 8px;

`;
const CategoryBadge = styled.span`
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  justify-content: center;
  padding: 4px 9px;
  border: 1px solid ${({$categoryColor,theme})=>$categoryColor || theme.colors.primary};
  border-radius: ${({theme})=>theme.radius.full};
  background: ${({theme})=>theme.colors.bgWhite};
  background: ${({$categoryColor,theme})=>`color-mix(in srgb, ${$categoryColor || theme.colors.primary} 14%, ${theme.colors.bgWhite})`};
  color: ${({theme})=>theme.colors.text};
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
`;
const MobileRequestMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px 12px;
  padding: 0 12px 11px;

  > div { min-width: 0; }
  > div.wide { grid-column: 1 / -1; }
  span { display: block; margin-bottom: 2px; color: ${({theme})=>theme.colors.textLight}; font-size: 10px; }
  strong { display: block; overflow-wrap: anywhere; color: ${({theme})=>theme.colors.text}; font-size: 12px; font-weight: 500; line-height: 1.45; }
`;
const MobileRequestActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid ${({theme})=>theme.colors.borderLight};
`;
const MobileMemoButton = styled.button`
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 7px 10px;
  border: 1px solid ${({theme})=>theme.colors.border};
  border-radius: ${({theme})=>theme.radius.sm};
  background: ${({theme})=>theme.colors.bgWhite};
  color: ${({theme})=>theme.colors.textSecondary};
  font-size: 11px;
`;
const GroupTbody = styled.tbody`
  background:#fff;
  & > tr > td{
    border-top:1px solid ${({theme})=>theme.colors.border};
    border-bottom:1px solid ${({theme})=>theme.colors.border};
  }
  & > tr > td:last-child{border-right:1px solid ${({theme})=>theme.colors.border};border-top-right-radius:${({theme})=>theme.radius.md};border-bottom-right-radius:${({theme})=>theme.radius.md};}
  & > tr:not(:last-child) > td:not([rowspan]){border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}
`;
const GroupRow = styled.tr`
  cursor:pointer;
  &:hover > td:not([rowspan]){background:#FAFBFC;}
`;
const SideCell = styled.td`
  width:220px;
  padding:0;
  vertical-align:top;
  background:#FAFBFC;
  border-left:4px solid ${({theme})=>theme.colors.primary};
  border-top:1px solid ${({theme})=>theme.colors.border} !important;
  border-bottom:1px solid ${({theme})=>theme.colors.border} !important;
  border-top-left-radius:${({theme})=>theme.radius.md};
  border-bottom-left-radius:${({theme})=>theme.radius.md};
`;
const SideCellInner = styled.div`
  padding:14px 16px;
  display:flex;flex-direction:column;gap:4px;height:100%;
`;
const MergedTd = styled.td`
  padding:12px 14px;
  font-size:12px;
  color:${({theme})=>theme.colors.textSecondary};
  vertical-align:middle;
  background:#FAFBFC;
  white-space:normal;
  word-break:break-word;
  line-height:1.5;
`;
const GroupSideName = styled.div`font-size:14px;font-weight:600;color:#1F2937;`;
const GroupSideMeta = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
const GroupSideCount = styled.div`margin-top:6px;font-size:11px;font-weight:500;color:${({theme})=>theme.colors.primary};`;
const GroupSideAll = styled.label`
  display:flex;align-items:center;gap:6px;margin-top:auto;padding-top:8px;
  font-size:11px;color:${({theme})=>theme.colors.textSecondary};cursor:pointer;
  input{cursor:pointer;}
`;
const Th = styled.th`background:#F9FAFB;padding:12px 14px;text-align:left;font-size:12px;font-weight:500;color:${({theme})=>theme.colors.textSecondary};border-bottom:1px solid ${({theme})=>theme.colors.border};white-space:nowrap;`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;
const RowAssignBtn = styled.button`
  display:inline-flex;align-items:center;gap:4px;
  padding:5px 12px;
  border:1px solid ${({theme})=>theme.colors.primary};
  background:#EEF2FF;
  color:${({theme})=>theme.colors.primary};
  border-radius:999px;
  font-size:12px;font-weight:600;
  cursor:pointer;
  &:hover{background:${({theme})=>theme.colors.primary};color:#fff;}
  @media(max-width:768px){min-height:40px;padding:6px 10px;border-radius:${({theme})=>theme.radius.sm};}
`;

const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;padding:4px 4px;@media(max-width:768px){align-items:stretch;flex-direction:column;gap:8px;}`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;

// --- Modal ---
const ModalOverlay = styled.div`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:1000;@media(max-width:768px){align-items:flex-end;padding:10px 10px calc(10px + env(safe-area-inset-bottom));}`;
const ModalContent = styled.div`background:#fff;border-radius:${({theme})=>theme.radius.lg};width:100%;max-width:480px;max-height:90vh;overflow-y:auto;box-shadow:${({theme})=>theme.shadow.lg};@media(max-width:768px){max-height:86vh;}`;
const ModalContentWide = styled(ModalContent)`max-width:640px;`;
const ModalHeader = styled.div`display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid ${({theme})=>theme.colors.border};@media(max-width:768px){padding:15px 16px;}`;
const RequestDetailModalContent = styled(ModalContentWide)`border-top:5px solid ${({$categoryColor,theme})=>$categoryColor || theme.colors.primary};`;
const RequestDetailHeader = styled(ModalHeader)`
  background: ${({theme})=>theme.colors.bgWhite};
  background: ${({$categoryColor,theme})=>`color-mix(in srgb, ${$categoryColor || theme.colors.primary} 9%, ${theme.colors.bgWhite})`};
`;
const RequestDetailHeading = styled.div`display:flex;min-width:0;align-items:center;gap:10px;`;
const ModalTitle = styled.h2`font-size:16px;font-weight:600;margin:0;`;
const CloseBtn = styled.button`background:none;border:none;color:${({theme})=>theme.colors.textLight};cursor:pointer;display:inline-flex;align-items:center;`;
const ModalBody = styled.div`padding:22px;@media(max-width:768px){padding:16px;}`;
const ModalFooter = styled.div`display:flex;justify-content:flex-end;gap:8px;padding:14px 22px;border-top:1px solid ${({theme})=>theme.colors.border};@media(max-width:768px){padding:12px 16px;>button{min-height:44px;flex:1;}}`;
const FieldLabel = styled.div`font-size:13px;font-weight:500;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const FieldValue = styled.div`font-size:14px;font-weight:500;color:#1F2937;`;
const FilterNote = styled.span`font-size:11px;font-weight:400;color:${({theme})=>theme.colors.primary};margin-left:6px;`;
const ToggleRow = styled.div`display:flex;align-items:center;gap:6px;margin-top:10px;font-size:12px;color:${({theme})=>theme.colors.textSecondary};label{cursor:pointer;}input{cursor:pointer;}`;
const AssignRowEmpty = styled.div`font-size:12px;color:#9A3412;padding:6px 10px;background:#FFF7ED;border:1px solid #FED7AA;border-radius:${({theme})=>theme.radius.sm};`;

const AssignModalContent = styled(ModalContent)`max-width:680px;`;
const ModalSubtitle = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-top:4px;`;

const CategoryTabs = styled.div`display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px;`;
const CategoryTab = styled.button`
  display:inline-flex;align-items:center;gap:6px;
  padding:6px 14px;
  border:1px solid ${({$active,theme})=>$active?theme.colors.primary:theme.colors.border};
  background:${({$active,theme})=>$active?theme.colors.primary:'#fff'};
  color:${({$active})=>$active?'#fff':'#374151'};
  border-radius:999px;
  font-size:13px;font-weight:500;
  cursor:pointer;
  transition:all .12s ease;
`;
const CategoryTabCount = styled.span`
  display:inline-flex;align-items:center;justify-content:center;
  min-width:18px;height:18px;padding:0 5px;
  background:${({$active})=>$active?'rgba(255,255,255,.25)':'#EEF2FF'};
  color:${({$active,theme})=>$active?'#fff':theme.colors.primary};
  border-radius:999px;
  font-size:11px;font-weight:600;
`;

const AssignBox = styled.div`background:#F0F7FF;border:1px dashed #93C5FD;border-radius:${({theme})=>theme.radius.md};padding:18px;`;
const AssignBoxHeader = styled.div`display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:4px;`;
const AssignBoxHeaderLeft = styled.div`display:flex;align-items:center;gap:8px;`;
const ActiveCatTitleChip = styled.span`display:inline-flex;align-items:center;padding:3px 10px;background:${({theme})=>theme.colors.primary};color:#fff;border-radius:999px;font-size:11px;font-weight:600;`;
const ActiveCatTitle = styled.span`font-size:15px;font-weight:600;color:#1F2937;`;
const CandidatePill = styled.span`display:inline-flex;align-items:center;padding:4px 10px;background:#fff;border:1px solid ${({theme})=>theme.colors.border};border-radius:999px;font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
const AssignBoxSubLine = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:14px;`;

const AssignFormRow = styled.div`display:flex;gap:14px;align-items:flex-end;margin-bottom:18px;`;
const AssignFormCol = styled.div`flex:1;min-width:0;`;
const AssignQtyBox = styled.div`min-width:80px;padding:8px 12px;background:#fff;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};text-align:center;`;
const AssignQtyLabel = styled.div`font-size:11px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:2px;`;
const AssignQtyValue = styled.div`font-size:20px;font-weight:700;color:#1F2937;`;

const SelectedReqHead = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;`;
const SelectedReqTitle = styled.div`display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:#1F2937;`;
const CountPill = styled.span`display:inline-flex;align-items:center;padding:2px 8px;background:#DBEAFE;color:${({theme})=>theme.colors.primary};border-radius:999px;font-size:11px;font-weight:600;`;
const SelectedReqActions = styled.div`display:flex;gap:6px;`;
const OutlineBtn = styled.button`background:#fff;border:1px solid ${({theme})=>theme.colors.primary};padding:5px 12px;font-size:12px;color:${({theme})=>theme.colors.primary};border-radius:${({theme})=>theme.radius.sm};cursor:pointer;font-weight:500;&:hover{background:#EFF6FF;}`;

const DashedHint = styled.div`padding:14px;border:1px dashed ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:12px;background:#fff;`;

const TargetListBox = styled.div`background:#fff;border:1px solid ${({theme})=>theme.colors.borderLight};border-radius:${({theme})=>theme.radius.sm};padding:12px 14px;margin-bottom:10px;`;
const TargetListInline = styled.div`display:flex;align-items:flex-start;gap:10px;`;
const TargetListLabel = styled.div`font-size:12px;font-weight:600;color:#1F2937;flex-shrink:0;padding-top:1px;`;
const TargetInlineText = styled.div`font-size:13px;color:#374151;line-height:1.6;`;
const TargetInlineMore = styled.span`color:${({theme})=>theme.colors.primary};font-weight:500;`;

const GhostBtn = styled.button`padding:8px 18px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;cursor:pointer;`;
const PrimaryBtn = styled.button`padding:8px 18px;border:none;background:${({theme})=>theme.colors.primary};color:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;font-weight:500;cursor:pointer;`;

const DetailSectionTitle = styled.div`font-size:13px;font-weight:700;color:#1F2937;margin:18px 0 10px;padding-bottom:6px;border-bottom:1px solid ${({theme})=>theme.colors.borderLight};&:first-child{margin-top:0;}`;
const DetailGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;`;
const DetailItem = styled.div`${({$full})=>$full?'grid-column:1/-1;':''}`;
const DetailLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:3px;`;
const DetailValue = styled.div`font-size:13px;color:#1F2937;font-weight:500;`;
const DetailServiceBadges = styled.div`display:flex;flex-wrap:wrap;gap:6px;`;
const MemoBox = styled.div`margin-top:14px;padding:12px 14px;background:#F9FAFB;border-radius:${({theme})=>theme.radius.sm};font-size:13px;line-height:1.6;color:${({theme})=>theme.colors.textSecondary};`;
